// Shared constants and helpers for the Email Configuration page.
//
// Keep this file free of React / MUI imports so it can be reused from both
// component and non-component code (e.g. selectors, thunks, tests).
// Pagination
export const EMAIL_CONFIG_PAGINATION = {
    DEFAULT_PAGE: 1,
    LIMIT: 20,
};
// Recipient label sets
export const TEAM_RECIPIENTS = new Set([
    'User', 'AM', 'Associated AM', 'Paid Social', 'Associated Paid Social',
    'Paid Search', 'Associated Paid Search', 'Design Lead',
    'Associated Design Lead', 'Email Lead', 'Associated Email Lead',
    'Affiliate Lead', 'Associate Affiliate Lead',
]);
export const CLIENT_RECIPIENTS = new Set(['Client', 'MC']);
export const SENDER_OPTIONS = [
    { value: 'team', label: 'Internal Team' },
    { value: 'client', label: 'Client' },
    { value: 'both', label: 'Client + Internal Team' },
    { value: 'system', label: 'System' },
    { value: 'user', label: 'User' },
];
export const DEFAULT_RECIPIENT_OPTIONS = [
    { value: 'Client', label: 'Client' },
    { value: 'MC', label: 'MC' },
    { value: 'User', label: 'User' },
    { value: 'AM', label: 'AM' },
    { value: 'Associated AM', label: 'Associated AM' },
    { value: 'Paid Social', label: 'Paid Social' },
    { value: 'Associated Paid Social', label: 'Associated Paid Social' },
    { value: 'Paid Search', label: 'Paid Search' },
    { value: 'Associated Paid Search', label: 'Associated Paid Search' },
    { value: 'Design Lead', label: 'Design Lead' },
    { value: 'Associated Design Lead', label: 'Associated Design Lead' },
    { value: 'Email Lead', label: 'Email Lead' },
    { value: 'Associated Email Lead', label: 'Associated Email Lead' },
    { value: 'Affiliate Lead', label: 'Affiliate Lead' },
    { value: 'Associate Affiliate Lead', label: 'Associate Affiliate Lead' },
];
// Display helpers
export const getSenderLabel = (sender) => {
    const option = SENDER_OPTIONS.find((o) => o.value === sender);
    return option?.label || '';
};
// Derives the Receiver column label from the list of default_recipient values.
export const getReceiverLabel = (defaultRecipient) => {
    if (!defaultRecipient || defaultRecipient.length === 0)
        return 'N/A';
    const hasTeam = defaultRecipient.some((r) => TEAM_RECIPIENTS.has(r));
    const hasClient = defaultRecipient.some((r) => CLIENT_RECIPIENTS.has(r));
    if (hasTeam && hasClient)
        return 'Internal Team & Client';
    if (hasTeam)
        return 'Internal Team';
    if (hasClient)
        return 'Client';
    return 'N/A';
};
export const formatNotificationKey = (key) => {
    if (!key)
        return '';
    return key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};
// Builds a `module_key -> module_name` map by walking the module tree once.
export const buildModuleNameMap = (modules) => {
    const map = new Map();
    const visit = (list) => {
        list.forEach((m) => {
            if (m?.key)
                map.set(m.key, m.module_name || m.key);
            if (m?.sub_modules?.length)
                visit(m.sub_modules);
        });
    };
    visit(modules || []);
    return map;
};
// Builds a `module_key -> module_name` map for the Module filter dropdown,
// excluding parent modules (containers that only group sub-modules). Only
// sub-modules and standalone (non-parent) modules are included.
export const buildFilterModuleNameMap = (modules) => {
    const map = new Map();
    const visit = (list) => {
        list.forEach((m) => {
            const isParent = m?.is_parent === true || (m?.sub_modules?.length ?? 0) > 0;
            if (m?.key && !isParent)
                map.set(m.key, m.module_name || m.key);
            if (m?.sub_modules?.length)
                visit(m.sub_modules);
        });
    };
    visit(modules || []);
    return map;
};
// Resolves a config's module display name from a pre-built map. Falls back to
// the formatted notification_key, then to an empty string.
export const resolveModuleDisplayName = (config, moduleNameMap) => {
    const key = config.module_key || '';
    if (!key)
        return '';
    return moduleNameMap.get(key) || formatNotificationKey(key);
};
export const formatUserNames = (users) => {
    if (!users || users.length === 0)
        return '';
    return users
        .map((u) => `${u.first_name} ${u.last_name}`.trim())
        .filter(Boolean)
        .join(', ');
};
